<template>
  <v-dialog
    :value="value"
    persistent
    max-width="640"
    content-class="ds-wizard-dialog"
    @input="$emit('input', $event)">
    <v-card flat class="ds-dialog">
      <div class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="primary">mdi-file-document-plus-outline</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <div class="ds-dialog__title">Start from scratch</div>
          <div class="ds-dialog__subtitle">
            Create an empty release you can write yourself — no Jira version needed.
          </div>
        </div>
        <v-btn icon :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="ds-dialog__body">
        <v-form ref="form" v-model="formValid" @submit.prevent="submit">
          <label for="manual-release-name" class="ds-field-label">Release name</label>
          <v-text-field
            id="manual-release-name"
            v-model="name"
            outlined
            dense
            autofocus
            placeholder="e.g. 1.2.0"
            counter="200"
            :rules="nameRules"
            :disabled="saving"></v-text-field>

          <label for="manual-release-markdown" class="ds-field-label">
            Release notes (Markdown, optional)
          </label>
          <v-textarea
            id="manual-release-markdown"
            v-model="markdown"
            outlined
            dense
            auto-grow
            rows="6"
            class="manual-release__markdown"
            placeholder="## Highlights&#10;- ..."
            :disabled="saving"></v-textarea>
          <div class="ds-dialog__hint">
            You can leave this empty and write the notes in the editor afterwards.
          </div>
        </v-form>
      </div>

      <div class="ds-dialog__footer">
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="saving" @click="submit">
          <v-icon left small>mdi-plus</v-icon>
          Create release
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import { error } from '@/utils/debugLogger';

  const NAME_MAX_LENGTH = 200;

  export default {
    name: 'ManualReleaseDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        name: '',
        markdown: '',
        formValid: false,
        saving: false,
        nameRules: [formRules.required, formRules.lengthMax(NAME_MAX_LENGTH)],
      };
    },
    watch: {
      value(opened) {
        if (opened) this.reset();
      },
    },
    methods: {
      reset() {
        this.name = '';
        this.markdown = '';
        this.saving = false;
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      },

      close() {
        if (this.saving) return;
        this.$emit('input', false);
      },

      async submit() {
        if (this.$refs.form && !this.$refs.form.validate()) return;

        this.saving = true;
        try {
          const markdown = this.markdown && this.markdown.trim() ? this.markdown : null;
          const created = await this.$store.dispatch('RELEASE_NOTES/CREATE_MANUAL_RELEASE', {
            name: this.name.trim(),
            markdown,
          });
          const releaseId = created ? created.id ?? created.releaseId : null;
          this.$dialog.notify.success('Release created.', {
            position: 'top-right',
            timeout: 3000,
          });
          this.$emit('input', false);
          this.$emit('created', releaseId);
        } catch (err) {
          // CREATE_MANUAL_RELEASE already surfaced the error via $handleError.
          error('[ManualReleaseDialog] Failed to create a manual release.', err);
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .manual-release__markdown ::v-deep textarea {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.8125rem;
    line-height: 1.5;
  }
</style>
